import styled from "styled-components";
import { useClientContactInformation } from "../../../graphql/hooks/client/useClientContactInformation";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import { theme } from "../../../theme";
import { Row } from "react-grid-system";
import Subheading from "../../atoms/typography/Subheading";
import { useTranslation } from "react-i18next";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { useState } from "react";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ContactCard from "../ContactCard";
import Alert from "../Alert";
import BodySmall from "../../atoms/typography/BodySmall";
import ClientAddresses from "./ClientAddresses";
import Small from "../../atoms/typography/Small";

interface Props {
  clientIdentifier: string;
}

const GreyContainer = styled.div`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey05};
  padding: 16px;
`;

const ClientGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const ContactContainer = styled.div`
  border-radius: 4px;
  border: 1px solid ${theme.colors.primaryBg};
  padding: 8px;
  display: flex;
  flex-direction: column;
  width: fit-content;
`;

const ClientOrgInfo = ({ clientIdentifier }: Props) => {
  const { t } = useTranslation();
  const { client, error, loading } =
    useClientContactInformation(clientIdentifier);

  const [displayOrganisation, setDisplayOrganisation] = useState(false);

  return (
    <div>
      {loading ? (
        <SmallLoadingSpinner />
      ) : (
        <div>
          <GreyContainer>
            <Row nogutter align="center" justify="between">
              <Subheading>{t("invoiceAssessment.organisation")}</Subheading>
              <ChevronButton
                type={displayOrganisation ? "up" : "down"}
                onClick={() => setDisplayOrganisation(!displayOrganisation)}
              />
            </Row>
            {error && (
              <Alert type="warning" title="Kunde inte hämta kontakter" small />
            )}
            <SlideOutHeight isVisible={displayOrganisation}>
              <HorizontalSpacer spacing={1} />
              <BodySmall fontweight={500}>Bolagsrepresentanter:</BodySmall>
              <ClientGridContainer>
                {client?.representatives?.map((contact, index) => (
                  <ContactCard
                    key={contact.firstName + index}
                    fullName={contact.firstName + " " + contact.lastName}
                    //phone={contact.phone}
                    clientAmlStatus={contact.amlStatus}
                    //email={contact.email}
                    role={contact.roles}
                  />
                ))}
                {!client?.representatives && (
                  <BodySmall>Inga bolagsrepresentanter hittades</BodySmall>
                )}
              </ClientGridContainer>
              <HorizontalSpacer spacing={1} />
              <BodySmall fontweight={500}>Addresser:</BodySmall>
              <HorizontalSpacer spacing={0.5} />
              <ClientGridContainer>
                <ClientAddresses
                  clientIdentifier={clientIdentifier}
                  useTitles={true}
                  useBackgroundColor={false}
                />
              </ClientGridContainer>

              <HorizontalSpacer spacing={1} />
              <BodySmall fontweight={500}>Epostkopplingar:</BodySmall>
              {client?.emailConnections &&
              client?.emailConnections?.length > 0 ? (
                <BodySmall>{client.emailConnections?.join(", ")}</BodySmall>
              ) : (
                <BodySmall>Inga epostkopplingar hittades</BodySmall>
              )}

              <HorizontalSpacer spacing={1} />
              <BodySmall fontweight={500}>Kontaktpersoner:</BodySmall>
              <ClientGridContainer>
                {client?.contacts?.map((contact, index) => (
                  <ContactContainer key={index}>
                    <Small fontweight={600}>{contact.name}</Small>
                    <Small>{contact.email}</Small>
                    <Small>{contact.phone}</Small>
                  </ContactContainer>
                ))}
                {(client?.contacts === undefined ||
                  client?.contacts?.length === 0) && (
                  <BodySmall>Inga kontaktpersoner hittades</BodySmall>
                )}
              </ClientGridContainer>
            </SlideOutHeight>
          </GreyContainer>
        </div>
      )}
    </div>
  );
};

export default ClientOrgInfo;
